import { Link } from "@tanstack/react-router";
import CheckCircle from "~icons/material-symbols/check-circle";
import Info from "~icons/material-symbols/info-outline";
import { css } from "../../../../styled-system/css";
import { t } from "../../../libs/i18n";
import type { R7ShigaOtsuCampaignBannerType } from "../utils/r7-shiga-otsu-campaign-banner";

type R7ShigaOtsuCampaignBannerProps = {
  bannerType: R7ShigaOtsuCampaignBannerType;
  onClickApply: () => void;
};

export const R7ShigaOtsuCampaignBanner = ({
  bannerType,
  onClickApply,
}: R7ShigaOtsuCampaignBannerProps) => {
  if (bannerType === "hidden") {
    return null;
  }

  return (
    <section
      className={css({
        bg: "surface.accentPrimaryLight",
        rounded: "xl",
        p: "16px",
        display: "flex",
        flexDir: "column",
        gap: "16px",
      })}
    >
      {bannerType === "not-applied" && (
        <NotAppliedContent onClickApply={onClickApply} />
      )}
      {bannerType === "applied" && <AppliedContent />}
      {bannerType === "campaign-ended" && <CampaignEndedContent />}

      <div
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        })}
      >
        <Link
          to="/about-campaign"
          className={css({
            flexGrow: 1,
            color: "text.accentPrimary",
            textDecoration: "underline",
          })}
        >
          {t("キャンペーン詳細")}
        </Link>
      </div>

      <NoticeSection />
    </section>
  );
};

const NotAppliedContent = ({ onClickApply }: { onClickApply: () => void }) => (
  <>
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "10px",
      })}
    >
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "6px",
        })}
      >
        <Info
          className={css({
            color: "text.accentPrimary",
            flexShrink: 0,
          })}
        />
        <p>{t("新規登録で必ず300ポイントプレゼント")}</p>
      </div>

      <p
        className={css({
          color: "text.secondary",
          fontSize: "14px",
        })}
      >
        {t(
          "「応募する」ボタンからのお申し込みで、その場で300ポイントをプレゼント！",
        )}
      </p>
    </div>

    <button
      type="button"
      className={css({
        bg: "surface.accentPrimary",
        rounded: "2xl",
        px: "14px",
        py: "12px",
        color: "white",
        fontWeight: 500,
      })}
      onClick={onClickApply}
    >
      {t("応募する")}
    </button>
  </>
);

const AppliedContent = () => (
  <>
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "10px",
      })}
    >
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "6px",
        })}
      >
        <Info
          className={css({
            color: "text.accentPrimary",
            flexShrink: 0,
          })}
        />
        <p>{t("新規登録キャンペーンは応募済みです")}</p>
      </div>
    </div>

    <button
      type="button"
      className={css({
        bg: "surface.disable",
        rounded: "2xl",
        px: "14px",
        py: "12px",
        color: "text.disable",
        fontWeight: 500,
      })}
      disabled
    >
      {t("応募する")}
    </button>

    <div
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
      })}
    >
      <CheckCircle
        className={css({
          color: "text.accentSuccess",
          width: "20px",
          height: "20px",
        })}
      />
      <p
        className={css({
          color: "text.accentSuccess",
          fontSize: "14px",
        })}
      >
        {t("応募済み")}
      </p>
    </div>
  </>
);

const CampaignEndedContent = () => (
  <div
    className={css({
      display: "flex",
      flexDir: "column",
      gap: "10px",
    })}
  >
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        gap: "6px",
      })}
    >
      <Info
        className={css({
          color: "text.accentPrimary",
          flexShrink: 0,
        })}
      />
      <p>{t("ポイントプレゼントキャンペーンは終了しました")}</p>
    </div>

    <p
      className={css({
        color: "text.secondary",
        fontSize: "14px",
      })}
    >
      {t("当選者数の上限に達したため、新規登録キャンペーンは終了しました。")}
    </p>
  </div>
);

const NoticeSection = () => (
  <div
    className={css({
      bg: "surface.primary",
      rounded: "lg",
      border: "1px solid",
      borderColor: "border.primary",
      p: "16px",
      display: "flex",
      flexDir: "column",
      gap: "8px",
    })}
  >
    <p
      className={css({
        fontWeight: "700",
        color: "text.primary",
      })}
    >
      {t("ご注意事項")}
    </p>
    <p
      className={css({
        color: "text.primary",
        fontSize: "14px",
        lineHeight: "1.6",
      })}
    >
      {t(
        "抽選ポイントは抽選会の応募にのみご利用いただけます（お買い物ポイントとは異なり、店舗でのご利用はできません。）。令和8年度第2回抽選会の応募期間は、令和9年3月（詳細日時は未定）です。抽選へのご参加には、1回あたり300ポイントが必要です。（※新規登録時に付与される300ポイントは、初回登録時のみ取得可能です。すでに過去の抽選会でポイントをご利用になった方は、別途イベント等にご参加いただき、300ポイントを貯めていただく必要がございます。）また、当選発表は、当選者に対するポケットおおつ内での通知の送付をもって代えさせていただきます。（※3月下旬頃の予定）なお、ポケットおおつに関する詳細情報や、抽選ポイント取得が可能なイベントについては、以下の大津市ホームページをご確認ください。",
      )}
    </p>
    <div
      className={css({
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        fontSize: "14px",
      })}
    >
      <a
        href="https://www.city.otsu.lg.jp/soshiki/001/1024/g/po/index.html"
        className={css({
          color: "text.accentPrimary",
          textDecoration: "underline",
        })}
        target="_blank"
        rel="noreferrer"
      >
        {t("大津市公式アプリ「ポケットおおつ」／大津市")}
      </a>
    </div>
  </div>
);
